import { useState } from 'react';
import { Button } from '@/design/components';
import { errorMessage } from '@/lib/errors';
import { isValidPhone, normalizePhone } from '@/lib/phone';
import type { EmployeeInput, EmployeeListItem } from './api';
import { useEmployeeOptions, useRolesForJobTitle } from './api';

type Values = {
  first_name: string;
  last_name: string;
  phone: string;
  email: string;
  birth_date: string;
  city: string;
  street: string;
  house_number: string;
  apartment: string;
  start_date: string;
  job_title_id: string;
  role_id: string;
  primary_branch_id: string;
  comax_employee_code: string;
  weekly_hours_target: string;
  monthly_hours_target: string;
};

/** הערך שהטופס מחזיר: שדות העובד, ובנפרד הסניפים הנוספים. */
export type EmployeeFormResult = {
  values: EmployeeInput;
  /** סניפים נוספים מעבר לראשי. ריק אצל רוב העובדים. */
  extraBranchIds: string[];
};

const EMPTY: Values = {
  first_name: '',
  last_name: '',
  phone: '',
  email: '',
  birth_date: '',
  city: '',
  street: '',
  house_number: '',
  apartment: '',
  start_date: '',
  job_title_id: '',
  role_id: '',
  primary_branch_id: '',
  comax_employee_code: '',
  weekly_hours_target: '',
  monthly_hours_target: '',
};

/**
 * טופס עובד (PRD 5.2).
 *
 * ת"ז ופרטי בנק אינם כאן אלא בלשונית נפרדת בכרטיס, מאחורי מפתח
 * hr.private ועם יומן גישה (מיגרציה 092). מי שעורך מכסת שעות
 * אינו אמור לראות אותם בדרך.
 */
export function EmployeeForm({
  employee,
  onCancel,
  onSubmit,
}: {
  /** הרשימה מביאה עכשיו את כל השדות, ולכן אין צורך בהשלמה חלקית. */
  employee?: EmployeeListItem;
  onCancel: () => void;
  onSubmit: (result: EmployeeFormResult) => Promise<void>;
}) {
  const { data: options, isPending } = useEmployeeOptions();
  const [values, setValues] = useState<Values>(() =>
    employee
      ? {
          ...EMPTY,
          first_name: employee.first_name,
          last_name: employee.last_name,
          phone: employee.phone,
          email: employee.email ?? '',
          birth_date: employee.birth_date ?? '',
          city: employee.city ?? '',
          street: employee.street ?? '',
          house_number: employee.house_number ?? '',
          apartment: employee.apartment ?? '',
          start_date: employee.start_date ?? '',
          job_title_id: employee.job_title_id ?? '',
          role_id: employee.role_id ?? '',
          primary_branch_id: employee.primary_branch_id ?? '',
          comax_employee_code: employee.comax_employee_code ?? '',
          weekly_hours_target: employee.weekly_hours_target?.toString() ?? '',
          monthly_hours_target: employee.monthly_hours_target?.toString() ?? '',
        }
      : EMPTY,
  );
  const [extraBranchIds, setExtraBranchIds] = useState<string[]>(
    () => (employee?.employee_branches ?? []).map((row) => row.branch_id),
  );
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // תפקיד ההרשאות נגזר מהתפקיד (מיגרציה 091): אין מצב שמוכר
  // מקבל הרשאות של הנהלה. מנהל סניף כן אפשרי בתפקיד רצפה.
  const allowedRoles = useRolesForJobTitle(values.job_title_id || null);
  const allowed = allowedRoles.data;

  /**
   * תפקיד הרשאות שאינו מותר בתפקיד שנבחר נחשב כאילו לא נבחר.
   *
   * נגזר ולא מנוקה ב effect: הרשימה מגיעה אחרי הבחירה, ואיפוס
   * ב effect היה מריץ רינדור נוסף על כל טעינה. בלי זה הטופס
   * נראה תקין ונשבר רק בשמירה, עם שגיאה על שדה שלא נגעו בו.
   */
  const roleId =
    allowed && values.role_id && !allowed.some((role) => role.id === values.role_id)
      ? ''
      : values.role_id;

  const set = (name: keyof Values) => (value: string) =>
    setValues((prev) => ({ ...prev, [name]: value }));

  const canSubmit =
    values.first_name.trim() !== '' &&
    values.last_name.trim() !== '' &&
    isValidPhone(values.phone) &&
    roleId !== '';

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setIsSaving(true);
    try {
      await onSubmit({
        extraBranchIds: extraBranchIds.filter((id) => id !== values.primary_branch_id),
        values: {
        first_name: values.first_name.trim(),
        last_name: values.last_name.trim(),
        phone: normalizePhone(values.phone)!,
        email: values.email.trim() || null,
        birth_date: values.birth_date || null,
        city: values.city.trim() || null,
        street: values.street.trim() || null,
        house_number: values.house_number.trim() || null,
        apartment: values.apartment.trim() || null,
        start_date: values.start_date || null,
        job_title_id: values.job_title_id || null,
        role_id: roleId,
        primary_branch_id: values.primary_branch_id || null,
        comax_employee_code: values.comax_employee_code.trim() || null,
        // ריק = בלי מכסה. אפס אינו "בלי מכסה" אלא "אסור לשבץ",
        // ולכן הוא לא מתקבל כאן.
        weekly_hours_target: values.weekly_hours_target
          ? Number(values.weekly_hours_target)
          : null,
        monthly_hours_target: values.monthly_hours_target
          ? Number(values.monthly_hours_target)
          : null,
        },
      });
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setIsSaving(false);
    }
  }

  if (isPending || !options) return <p className="text-body-sm text-muted">טוען</p>;

  return (
    <form onSubmit={handleSubmit} className="grid gap-4 rounded-md border border-line p-4" noValidate>
      <div className="grid gap-3 sm:grid-cols-2">
        <Text label="שם פרטי" required value={values.first_name} onChange={set('first_name')} />
        <Text label="שם משפחה" required value={values.last_name} onChange={set('last_name')} />
        <Text
          label="טלפון"
          required
          dir="ltr"
          value={values.phone}
          onChange={set('phone')}
          hint={
            employee
              ? 'שינוי המספר מעדכן גם את חשבון הכניסה של העובד'
              : 'למספר הזה תישלח הודעת ברוכים הבאים'
          }
        />
        <Text label="מייל" dir="ltr" value={values.email} onChange={set('email')} />

        <Select
          label="סניף"
          value={values.primary_branch_id}
          onChange={set('primary_branch_id')}
          options={options.branches}
          empty="ללא סניף"
        />
        <div className="grid content-start gap-1 sm:col-span-2">
          <span className="text-label font-medium">סניפים נוספים</span>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {options.branches
              .filter((branch) => branch.id !== values.primary_branch_id)
              .map((branch) => (
                <label
                  key={branch.id}
                  className="flex min-h-11 cursor-pointer items-center gap-2 text-body"
                >
                  <input
                    type="checkbox"
                    checked={extraBranchIds.includes(branch.id)}
                    onChange={(event) =>
                      setExtraBranchIds((prev) =>
                        event.target.checked
                          ? [...prev, branch.id]
                          : prev.filter((id) => id !== branch.id),
                      )
                    }
                    className="size-5"
                  />
                  {branch.name}
                </label>
              ))}
          </div>
          <p className="m-0 text-body-sm text-muted">
            עובד/ת שמסתובב/ת בין סניפים. הסידור של כל סניף מסומן יציע אותו/ה, והוא/היא
            יראו את ההודעות של הסניפים האלה.
          </p>
        </div>

        <Select
          label="תפקיד"
          value={values.job_title_id}
          onChange={set('job_title_id')}
          options={options.jobTitles}
          empty="ללא תפקיד"
          hint="קובע גם אילו הרשאות אפשר לתת"
        />
        <Select
          label="תפקיד הרשאות"
          required
          value={roleId}
          onChange={set('role_id')}
          options={allowed ?? []}
          empty="בחר/י"
          hint={
            values.job_title_id === ''
              ? 'קובע מה העובד רואה ועושה במערכת'
              : 'מוצגות רק ההרשאות שהתפקיד מתיר. את התקרה משנים במסך התפקידים'
          }
        />

        <Date label="תאריך תחילת עבודה" value={values.start_date} onChange={set('start_date')} />
        <Date label="תאריך לידה" value={values.birth_date} onChange={set('birth_date')} />
        {/* כתובת מחולקת (מיגרציה 094): address עצמו נבנה בשרת
            מהחלקים, ולכן אין כאן שדה חופשי שיתנגש איתו */}
        <div className="grid gap-3 sm:col-span-2 sm:grid-cols-4">
          <Text label="עיר" value={values.city} onChange={set('city')} />
          <Text label="רחוב" value={values.street} onChange={set('street')} />
          <Text label="מספר בית" value={values.house_number} onChange={set('house_number')} />
          <Text label="מספר דירה" value={values.apartment} onChange={set('apartment')} />
        </div>
        <Text
          label="קוד עובד"
          dir="ltr"
          value={values.comax_employee_code}
          onChange={set('comax_employee_code')}
          hint="המספר צריך להיות מספר עובד, לצורך קישור נתוני מכירות וביצועים נוספים"
        />

        <NumberField
          label="מכסת שעות שבועית"
          value={values.weekly_hours_target}
          onChange={set('weekly_hours_target')}
          min={1}
          max={168}
          hint="ריק = בלי מכסה. מוצג בסידור מול השעות ששובצו"
        />
        <NumberField
          label="מכסת שעות חודשית"
          value={values.monthly_hours_target}
          onChange={set('monthly_hours_target')}
          min={1}
          max={744}
          hint="ריק = בלי מכסה. אפשר לדרוס לחודש מסוים מתוך הסידור"
        />
      </div>

      {error && (
        <p role="alert" className="m-0 text-body-sm text-bad">
          {error}
        </p>
      )}

      <div className="flex gap-2">
        <Button type="submit" variant="primary" disabled={isSaving || !canSubmit}>
          {isSaving ? 'שומר' : employee ? 'שמירת השינויים' : 'הוספת עובד/ת'}
        </Button>
        <Button variant="secondary" onClick={onCancel}>
          ביטול
        </Button>
      </div>
    </form>
  );
}

type BaseProps = {
  label: string;
  value: string;
  onChange: (next: string) => void;
  required?: boolean;
  hint?: string;
};

function Field({
  label,
  required,
  hint,
  id,
  children,
}: BaseProps & { id: string; children: React.ReactNode }) {
  return (
    <div className="grid gap-1">
      <label htmlFor={id} className="text-label font-medium">
        {label}
        {required && <span aria-hidden="true"> *</span>}
      </label>
      {children}
      {hint && (
        <p id={`${id}-hint`} className="m-0 text-body-sm text-muted">
          {hint}
        </p>
      )}
    </div>
  );
}

const INPUT =
  'min-h-11 rounded-sm border border-muted bg-surface px-3 text-body text-ink';

function Text({ dir, ...props }: BaseProps & { dir?: 'ltr' }) {
  const id = `emp-${props.label}`;
  return (
    <Field {...props} id={id}>
      <input
        id={id}
        type="text"
        dir={dir}
        value={props.value}
        onChange={(event) => props.onChange(event.target.value)}
        aria-describedby={props.hint ? `${id}-hint` : undefined}
        className={INPUT}
      />
    </Field>
  );
}

function NumberField({ min, max, ...props }: BaseProps & { min?: number; max?: number }) {
  const id = `emp-${props.label}`;
  return (
    <Field {...props} id={id}>
      <input
        id={id}
        type="number"
        inputMode="numeric"
        dir="ltr"
        min={min}
        max={max}
        step={1}
        value={props.value}
        onChange={(event) => props.onChange(event.target.value)}
        aria-describedby={props.hint ? `${id}-hint` : undefined}
        className={INPUT}
      />
    </Field>
  );
}

function Date(props: BaseProps) {
  const id = `emp-${props.label}`;
  return (
    <Field {...props} id={id}>
      <input
        id={id}
        type="date"
        dir="ltr"
        value={props.value}
        onChange={(event) => props.onChange(event.target.value)}
        className={INPUT}
      />
    </Field>
  );
}

function Select({
  options,
  empty,
  ...props
}: BaseProps & { options: { id: string; name: string }[]; empty: string }) {
  const id = `emp-${props.label}`;
  return (
    <Field {...props} id={id}>
      <select
        id={id}
        value={props.value}
        onChange={(event) => props.onChange(event.target.value)}
        aria-describedby={props.hint ? `${id}-hint` : undefined}
        className={INPUT}
      >
        <option value="">{empty}</option>
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.name}
          </option>
        ))}
      </select>
    </Field>
  );
}
